// src/components/quizzes/BonusVA5Regions.js
//
// Virginia's 5 regions bonus round: catch the name, or the region that matches the clue.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell from "./kit/BonusShell";

const SPAWN_MS = 480;
const MIN_TARGETS_ONSCREEN = 2;
const TARGET_SPEED_SLOWDOWN = 0.92;

/** ======= VIRGINIA REGIONS (from your lyrics) ======= */
const REGIONS = [
  {
    name: "Coastal Plain (Tidewater)",
    short: "Coastal Plain",
    clue: "low flat land",
    emoji: "🏖️",
  },
  {
    name: "Piedmont",
    short: "Piedmont",
    clue: "rolling hills",
    emoji: "🌾",
  },
  {
    name: "Blue Ridge Mountains",
    short: "Blue Ridge",
    clue: "rounded part of the Appalachian Mountains",
    emoji: "⛰️",
  },
  {
    name: "Valley and Ridge",
    short: "Valley & Ridge",
    clue: "valleys along the Blue Ridge Mountain",
    emoji: "🏞️",
  },
  {
    name: "Appalachian Plateau",
    short: "Appalachian Plateau",
    clue: "high lands",
    emoji: "🗻",
  },
];

// small helpers
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// one of two prompt styles: by name or by clue
const makePrompt = () => {
  const mode = Math.random() < 0.55 ? "name" : "clue";
  const region = pick(REGIONS);
  return { mode, region };
};

const shouldSpawnCorrectBalanced = (targetRegion, items) => {
  const correct = items.filter((it) => it.region.name === targetRegion.name).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const newPrompt = (prev) => {
  let next = makePrompt();
  for (let i = 0; i < 8 && prev && next.region.name === prev.region.name; i += 1) next = makePrompt();
  return next;
};

const PlacesBonus = (props) => (
  <BonusShell
    {...props}
    title="Virginia's Regions!"
    emoji="🗺️"
    sub="Catch the name shown at the top — or the place that matches the clue."
    intro="Virginia's five regions! Catch the region shown at the top, or the region that matches the clue. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "The right place", pts: "+5" },
      { icon: "❌", text: "A different place", pts: "-3" },
    ]}
    init={newPrompt}
    prompt={(p) =>
      p.mode === "name"
        ? { big: p.region.short, small: "Catch this one!", say: `Catch ${p.region.short}.` }
        : { big: p.region.clue, small: "Which place is this? Catch it!", say: `Which one is: ${p.region.clue}?` }
    }
    spawn={(p, onScreen) => {
      const want = shouldSpawnCorrectBalanced(p.region, onScreen.map((x) => ({ region: x.value })));
      return want ? p.region : pick(REGIONS.filter((u) => u.name !== p.region.name));
    }}
    check={(p, v) => v.name === p.region.name}
    label={(v) => (
      <span>
        <span style={{ fontSize: 26 }}>{v.emoji}</span> {v.short}
      </span>
    )}
    shape="chip"
    itemSize={72}
    badPoints={3}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
    caughtLabel="Places"
  />
);

export default PlacesBonus;
